'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { ArrowLeft, User, Mail, Phone, Loader2, Shield, Sparkles } from 'lucide-react';
import { getSessionId, getAnswersFromSession } from '@/lib/session';

interface FormData {
  firstName: string;
  age: string;
  gender: string;
  email: string;
  whatsapp: string;
}

const genderOptions = [
  { id: 'gender-female', label: 'Female' },
  { id: 'gender-male', label: 'Male' },
  { id: 'gender-nonbinary', label: 'Non-binary' },
  { id: 'gender-other', label: 'Other' },
  { id: 'gender-prefer-not', label: 'Prefer not to say' },
];

const GENERATION_STEPS = [
  'Reading your questionnaire responses…',
  'Mapping your patterns and goals…',
  'Shaping a plan around your pace and preferences…',
  'Preparing your morning and evening practices…',
  'Writing your personalised meditation scripts…',
  'Putting the finishing touches on your plan…',
];

export default function DetailCaptureForm() {
  const router = useRouter();
  const [submitting, setSubmitting] = useState(false);
  const [generationStep, setGenerationStep] = useState(0);
  const [selectedGender, setSelectedGender] = useState('');
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    age: '',
    gender: '',
    email: '',
    whatsapp: '',
  });

  function setField(field: keyof FormData, value: string) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  function onGenderSelect(value: string) {
    setSelectedGender(value);
    setField('gender', value);
  }

  function validate(): boolean {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'Please enter your first name';
    if (!formData.age) {
      newErrors.age = 'Please enter your age';
    } else if (Number(formData.age) < 13 || Number(formData.age) > 99) {
      newErrors.age = 'Please enter a valid age (13–99)';
    }
    if (!formData.gender) newErrors.gender = 'Please select a gender option';
    if (!formData.email.trim()) {
      newErrors.email = 'Please enter your email address';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    const sessionId = getSessionId();
    const questionnaireAnswers = getAnswersFromSession();

    // Cycle through generation steps while waiting
    let stepIdx = 0;
    const stepInterval = setInterval(() => {
      stepIdx = Math.min(stepIdx + 1, GENERATION_STEPS.length - 1);
      setGenerationStep(stepIdx);
    }, 2200);

    try {
      const res = await fetch('/api/users/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.firstName,
          age: parseInt(formData.age, 10),
          gender: formData.gender,
          email: formData.email,
          whatsapp: formData.whatsapp || undefined,
          sessionId: sessionId || undefined,
          questionnaireAnswers: questionnaireAnswers || undefined,
        }),
      });

      clearInterval(stepInterval);

      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        // Store the generated plan in sessionStorage for the practice home screen
        if (data.fullPlan) {
          sessionStorage.setItem('blv_full_plan', JSON.stringify(data.fullPlan));
          localStorage.setItem('blv_full_plan', JSON.stringify(data.fullPlan));
        }
        if (data.scripts) {
          sessionStorage.setItem('blv_scripts', JSON.stringify(data.scripts));
          localStorage.setItem('blv_scripts', JSON.stringify(data.scripts));
        }
        if (data.userId) {
          sessionStorage.setItem('blv_user_id', data.userId);
          localStorage.setItem('blv_user_id', data.userId);
        }
        // Store first name for personalised greeting
        sessionStorage.setItem('blv_first_name', formData.firstName);
        localStorage.setItem('blv_first_name', formData.firstName);
      } else {
        const data = await res.json().catch(() => ({}));
        console.error('[blv] account creation failed:', data?.error);
        clearInterval(stepInterval);
      }
    } catch (err) {
      console.error('[blv] account creation request error:', err);
      clearInterval(stepInterval);
    }

    router.push('/your-reset-practice-home');
  }

  if (submitting) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-[#FAF8F5]">
        <AppLogo size={52} className="mb-8 pulse-soft" />
        <h2 className="text-2xl font-bold text-[#1C1917] mb-3 text-center">
          Building your personalised plan
        </h2>
        <p className="text-[#6B6560] text-center max-w-sm leading-relaxed mb-10">
          {GENERATION_STEPS[generationStep]}
        </p>
        <div className="w-64 h-1.5 bg-[#EAE6E0] rounded-full overflow-hidden mb-8">
          <div
            className="h-full bg-gradient-to-r from-[#2A9D8F] to-[#52B788] rounded-full transition-all duration-1000"
            style={{
              width: `${Math.round(((generationStep + 1) / GENERATION_STEPS.length) * 100)}%`,
            }}
          />
        </div>
        <div className="max-w-xs text-center space-y-3">
          <p className="text-sm text-[#9B9590] italic">
            "We're shaping a plan around your goals, patterns, and pace."
          </p>
          <p className="text-xs text-[#C5C0BA]">
            Your morning and evening practices are being prepared.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top bar */}
      <div className="sticky top-0 z-50 bg-[#FAF8F5]/95 backdrop-blur-md border-b border-[#EAE6E0]">
        <div className="max-w-screen-sm mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/personalised-plan-preview" className="flex items-center gap-2 text-[#6B6560] hover:text-[#2A9D8F] transition-colors">
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">Back to plan</span>
          </Link>
          <div className="flex items-center gap-2">
            <AppLogo size={26} />
            <span className="font-bold text-base text-[#1C1917]">blv</span>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-screen-sm mx-auto w-full px-6 pt-10 pb-10">
        {/* Header */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 bg-[#52B788]/10 rounded-full px-3 py-1.5 text-xs font-semibold text-[#52B788] mb-5">
            <Sparkles size={12} />
            Save your personalised experience
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1917] mb-3">
            Let&apos;s make this truly yours.
          </h1>
          <p className="text-[#6B6560] leading-relaxed">
            These details let us save your personalised plan, generate your morning and evening
            practices, and deliver them directly to you. No password. No spam.
          </p>
        </div>

        {/* Privacy note */}
        <div className="flex items-start gap-3 bg-white border border-[#EAE6E0] rounded-2xl p-4 mb-8">
          <Shield size={16} className="text-[#2A9D8F] mt-0.5 flex-shrink-0" />
          <p className="text-xs text-[#6B6560] leading-relaxed">
            Your data is stored securely and never shared or sold. We use your name and preferences
            only to personalise your blv experience. You can delete your account at any time.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-6">
          {/* First name */}
          <div>
            <label className="block text-sm font-semibold text-[#1C1917] mb-1.5" htmlFor="firstName">
              First name
            </label>
            <p className="text-xs text-[#9B9590] mb-2">We use this to personalise your practice.</p>
            <div className="relative">
              <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9B9590]" />
              <input
                id="firstName"
                type="text"
                placeholder="e.g. Priya"
                value={formData.firstName}
                onChange={(e) => setField('firstName', e.target.value)}
                className="w-full bg-white border border-[#D5D0CA] rounded-xl pl-10 pr-4 py-3 text-base text-[#1C1917] placeholder-[#C5C0BA] focus:outline-none focus:border-[#2A9D8F] focus:ring-2 focus:ring-[#2A9D8F]/10 transition-all"
              />
            </div>
            {errors.firstName && (
              <p className="text-xs text-red-500 mt-1.5">{errors.firstName}</p>
            )}
          </div>

          {/* Age */}
          <div>
            <label className="block text-sm font-semibold text-[#1C1917] mb-1.5" htmlFor="age">
              Age
            </label>
            <p className="text-xs text-[#9B9590] mb-2">Helps us calibrate the language and depth of your content.</p>
            <input
              id="age"
              type="number"
              min="13"
              max="99"
              placeholder="e.g. 32"
              value={formData.age}
              onChange={(e) => setField('age', e.target.value)}
              className="w-full bg-white border border-[#D5D0CA] rounded-xl px-4 py-3 text-base text-[#1C1917] placeholder-[#C5C0BA] focus:outline-none focus:border-[#2A9D8F] focus:ring-2 focus:ring-[#2A9D8F]/10 transition-all"
            />
            {errors.age && (
              <p className="text-xs text-red-500 mt-1.5">{errors.age}</p>
            )}
          </div>

          {/* Gender */}
          <div>
            <label className="block text-sm font-semibold text-[#1C1917] mb-1.5">
              Gender
            </label>
            <p className="text-xs text-[#9B9590] mb-3">Used to match the narrator voice for your audio sessions.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {genderOptions.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => onGenderSelect(opt.label)}
                  className={`py-2.5 px-3 rounded-xl text-sm font-medium border transition-all text-center ${
                    selectedGender === opt.label
                      ? 'border-[#2A9D8F] text-[#1C6B62]'
                      : 'border-[#D5D0CA] bg-white text-[#4A4540] hover:border-[#2A9D8F]/50'
                  }`}
                  style={{
                    background: selectedGender === opt.label ? 'rgba(42,157,143,0.08)' : undefined,
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {errors.gender && (
              <p className="text-xs text-red-500 mt-1.5">{errors.gender}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-[#1C1917] mb-1.5" htmlFor="email">
              Email address
            </label>
            <p className="text-xs text-[#9B9590] mb-2">We&apos;ll send your practice here. No spam, ever.</p>
            <div className="relative">
              <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9B9590]" />
              <input
                id="email"
                type="email"
                placeholder="e.g. priya@gmail.com"
                value={formData.email}
                onChange={(e) => setField('email', e.target.value)}
                className="w-full bg-white border border-[#D5D0CA] rounded-xl pl-10 pr-4 py-3 text-base text-[#1C1917] placeholder-[#C5C0BA] focus:outline-none focus:border-[#2A9D8F] focus:ring-2 focus:ring-[#2A9D8F]/10 transition-all"
              />
            </div>
            {errors.email && (
              <p className="text-xs text-red-500 mt-1.5">{errors.email}</p>
            )}
          </div>

          {/* WhatsApp */}
          <div>
            <label className="block text-sm font-semibold text-[#1C1917] mb-1.5" htmlFor="whatsapp">
              WhatsApp number
              <span className="ml-2 text-xs font-normal text-[#9B9590]">(optional)</span>
            </label>
            <p className="text-xs text-[#9B9590] mb-2">
              For PLUS and PREMIUM members, we deliver daily resets directly to WhatsApp.
            </p>
            <div className="relative">
              <Phone size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9B9590]" />
              <input
                id="whatsapp"
                type="tel"
                placeholder="e.g. +44 7700 900123"
                value={formData.whatsapp}
                onChange={(e) => setField('whatsapp', e.target.value)}
                className="w-full bg-white border border-[#D5D0CA] rounded-xl pl-10 pr-4 py-3 text-base text-[#1C1917] placeholder-[#C5C0BA] focus:outline-none focus:border-[#2A9D8F] focus:ring-2 focus:ring-[#2A9D8F]/10 transition-all"
              />
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full text-base mt-2"
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Building your plan…
              </>
            ) : (
              <>
                <Sparkles size={18} />
                Generate my free personalised plan
              </>
            )}
          </button>

          <p className="text-center text-xs text-[#9B9590]">
            By continuing, you agree to blv&apos;s{' '}
            <span className="underline cursor-pointer">Terms</span> and{' '}
            <span className="underline cursor-pointer">Privacy Policy</span>.
            We&apos;ll use a secure link to sign you in — no password needed.
          </p>
        </form>
      </main>
    </div>
  );
}